import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { NormalizedSchema } from 'normalizr';
import { normalize } from 'normalizr';
import type {
	IMetaProduct,
	IMetaProductNormalizeResult,
} from '~/Slices/schema';
import { metaProductEntity } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import type * as Domain from '~/domain';
import { ApiRequestError, ApiServerError, fetch2, unwrapFetch2 } from '~/fetch';
import { Customer } from '~/helpers';
import { endpoint } from '~/helpers/Endpoints';
import * as Hub from '~/ogm-hub';
import {
	MetaProductGroupingSource,
	MetaProductGroupingStatus,
} from '~/ogm-hub';

interface IFetchMetaProductParameters {
	metaProductId: number;
	blueprintId: number;
}

interface ISaveMetaProductParameters {
	metaProduct: IMetaProduct;
	blueprintId: number;
	applyRelatedGrouping?: boolean;
	groupingState?: MetaProductGroupingStatus;
	groupingSource?: MetaProductGroupingSource;
	recalculateOptimization?: boolean;
}

interface IDeleteMetaProductParameters {
	metaProductId: number;
	blueprintId: number;
}

export const fetchMetaProduct = createAsyncThunk<
	NormalizedSchema<IMetaProductNormalizeResult, string>,
	IFetchMetaProductParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProducts/fetchMetaProduct',
	async (params: IFetchMetaProductParameters, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				fetch2<Domain.IMetaProduct>({
					uri: endpoint.metaProducts({
						mpid: params.metaProductId,
						bpid: params.blueprintId,
					}),
					method: 'GET',
				})
			);
			return normalize<any, IMetaProductNormalizeResult>(
				result,
				metaProductEntity
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const saveMetaProduct = createAsyncThunk<
	IMetaProduct,
	ISaveMetaProductParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProducts/saveMetaProduct',
	async (params: ISaveMetaProductParameters, { rejectWithValue }) => {
		try {
			const { metaProduct, blueprintId } = params;

			const metaProductItems = metaProduct.products.map((product, index) => {
				const metaProductItemId = metaProduct.isSelectByUm
					? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
					: `${product.corpSupplierCode}|${product.supplierProductNumber}`;
				return {
					supplierProductNumber: product.supplierProductNumber,
					specIndicatorId:
						metaProduct.productSpecIndicatorIds[metaProductItemId] || 0,
					itemOrder: index + 1,
					unitMeasureCode: product.unitMeasureCode,
				};
			});

			const customCategoryId = Customer.customCategoriesEnabled()
				? metaProduct.customCategoryId
				: null;

			const body = JSON.stringify({
				name: metaProduct.name.trim(),
				search: metaProduct.lastSearchTerm,
				blueprintId,
				metaProductItems,
				isSelectByUm: metaProduct.isSelectByUm,
				selectionRuleSetId: metaProduct.selectionRuleSetId,
				maintainedBy: metaProduct.maintainedBy || 1,
				statusId: metaProduct.statusId || 1,
				specIndicatorId: metaProduct.specIndicatorId || 0,
				distributionCenterOverrides:
					metaProduct.distributionCenterPriorityOverrides,
				customCategoryId,
				recalculateOptimization: params.recalculateOptimization ?? true,
			});

			if (params.metaProduct.id != null) {
				await unwrapFetch2(
					fetch2({
						uri: endpoint.metaProduct(params.metaProduct.id),
						method: 'PUT',
						body,
					})
				);

				if (params.applyRelatedGrouping) {
					const isMachine =
						params.groupingSource == MetaProductGroupingSource.Machine ||
						params.groupingSource == MetaProductGroupingSource.Ai;
					if (
						params.groupingState === MetaProductGroupingStatus.New &&
						isMachine
					) {
						await unwrapFetch2(
							Hub.MetaProductController.StageMetaProductGroup({
								metaProductId: params.metaProduct.id,
							})
						);
					} else if (
						params.groupingState === MetaProductGroupingStatus.Staged ||
						!isMachine
					) {
						await unwrapFetch2(
							Hub.MetaProductController.AcceptMetaProductGroup({
								metaProductId: params.metaProduct.id,
							})
						);
					}
				}

				return params.metaProduct;
			} else {
				const result = await unwrapFetch2<Domain.IMetaProduct>(
					fetch2({
						uri: endpoint.metaProducts(),
						method: 'POST',
						body,
					})
				);

				if (params.applyRelatedGrouping) {
					await unwrapFetch2(
						Hub.MetaProductController.AcceptMetaProductGroup({
							metaProductId: params.metaProduct.id,
						})
					);
				}

				return {
					...params.metaProduct,
					id: result.id,
				};
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const deleteMetaProduct = createAsyncThunk<
	number,
	IDeleteMetaProductParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProducts/deleteMetaProduct',
	async (params: IDeleteMetaProductParameters, { rejectWithValue }) => {
		try {
			await unwrapFetch2(
				Hub.MetaProductController.DeleteMetaProductByBPID(params)
			);
			return params.metaProductId;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

const metaProductsAdapter = createEntityAdapter<IMetaProduct>();

export const metaProductsSlice = createSlice({
	name: 'metaProducts',
	initialState: metaProductsAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchMetaProduct.fulfilled, (state, action) => {
			metaProductsAdapter.upsertMany(
				state,
				action.payload.entities.metaProducts
			);
		});
		builder.addCase(saveMetaProduct.fulfilled, (state, action) => {
			metaProductsAdapter.upsertOne(state, action.payload);
		});
		builder.addCase(deleteMetaProduct.fulfilled, (state, action) => {
			metaProductsAdapter.removeOne(state, action.payload);
		});
	},
});

export function selectProductHeadersForMetaProduct(state, id: number) {
	const productIds = state.metaProducts.entities[id].products;
	return productIds.map(
		(productId) => state.productHeaders.entities[productId]
	);
}
